/**
 * MoonPhaseReveal — MP3 screen of the Moon Phase Soulmate flow.
 *
 * Route: /flow/moon-phase-soulmate/reveal
 *
 * Layout:
 *   - CosmosBackdrop (decorative)
 *   - Eyebrow: "YOUR SOULMATE'S PHASE"
 *   - LivingSigil size=220 state="reveal" + small moon phase glyph overlay
 *   - Display title: phaseName
 *   - Archetype name (prominently)
 *   - Blurb paragraph
 *   - Illumination + lunarAge readout (monospace, ALL-CAPS labels)
 *   - CTA "Share the rite" → /flow/moon-phase-soulmate/share
 *   - Secondary "Return to catalog" → /catalog
 *
 * Data:
 *   Reads output from useLocation().state. If absent (deep-link / refresh),
 *   re-fetches via useRunMoonPhase (backend returns cached:true within 24h).
 *
 * Accessibility:
 *   - aria-live="polite" region announcing "Your soulmate is {archetypeName}"
 *   - Haptic on mount via Telegram WebApp API
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { CosmosBackdrop } from '../../brand/CosmosBackdrop';
import { LivingSigil } from '../../brand/LivingSigil';
import { useRunMoonPhase } from '../../../hooks/useRunMoonPhase';
import type { MoonPhaseOutput } from '../../../hooks/useRunMoonPhase';
import styles from './MoonPhase.module.css';

// --------------------------------------------------------------------------
// Moon phase glyph lookup
// 8 canonical phases → unicode glyph
// --------------------------------------------------------------------------

const PHASE_GLYPHS: Record<string, string> = {
  new_moon:          '🌑',
  waxing_crescent:   '🌒',
  first_quarter:     '🌓',
  waxing_gibbous:    '🌔',
  full_moon:         '🌕',
  waning_gibbous:    '🌖',
  third_quarter:     '🌗',
  waning_crescent:   '🌘',
};

function getMoonGlyph(phase: string): string {
  // Normalise: lowercase + underscores
  const key = phase.toLowerCase().replace(/[\s-]+/g, '_');
  return PHASE_GLYPHS[key] ?? '🌕';
}

// --------------------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------------------

interface RevealBodyProps {
  output: MoonPhaseOutput;
}

function RevealBody({ output }: RevealBodyProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('flows');

  function handleShare() {
    void navigate('/flow/moon-phase-soulmate/share', { state: { output } });
  }

  function handleCatalog() {
    void navigate('/catalog');
  }

  const glyph = getMoonGlyph(output.phase);
  const illuminationPct = `${Math.round(output.illumination * 100)}%`;
  const lunarAgeDays = `${Number(output.lunarAge).toFixed(1)}d`;

  return (
    <>
      {/* aria-live region — announces archetype on mount */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="mp-reveal-arialive"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          borderWidth: 0,
        }}
      >
        {t('moonPhase.reveal.soulmateAnnounce', { name: output.archetypeName })}
      </div>

      {/* Eyebrow */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span className={styles.eyebrow} data-testid="mp-reveal-eyebrow">
          {t('moonPhase.reveal.eyebrow')}
        </span>
      </div>

      {/* Sigil + moon glyph overlay */}
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
        <div className={styles.sigilWrap} data-testid="mp-reveal-sigil">
          <LivingSigil size={220} state="reveal" archetype="moon_phase_soulmate" />
        </div>
        <span
          className={styles.phaseGlyph}
          aria-label={t('moonPhase.reveal.phaseAria', { phase: output.phaseName })}
          data-testid="mp-reveal-phase-glyph"
          style={{
            position: 'absolute',
            bottom: 8,
            right: 'calc(50% - 110px + 8px)',
          }}
        >
          {glyph}
        </span>
      </div>

      {/* Reveal header block */}
      <div className={styles.revealHeader}>
        <h1 className={styles.displayTitle} data-testid="mp-reveal-phase-name">
          {output.phaseName}
        </h1>
        <p className={styles.archetypeName} data-testid="mp-reveal-archetype-name">
          {output.archetypeName}
        </p>
        <p className={styles.revealBlurb} data-testid="mp-reveal-blurb">
          {output.archetypeBlurb}
        </p>
      </div>

      {/* Readout row */}
      <div className={styles.readoutRow}>
        <div className={styles.readoutItem}>
          <span className={styles.readoutLabel}>{t('moonPhase.reveal.illuminationLabel')}</span>
          <span className={styles.readoutValue} data-testid="mp-reveal-illumination">
            {illuminationPct}
          </span>
        </div>
        <div className={styles.readoutItem}>
          <span className={styles.readoutLabel}>{t('moonPhase.reveal.lunarAgeLabel')}</span>
          <span className={styles.readoutValue} data-testid="mp-reveal-lunar-age">
            {lunarAgeDays}
          </span>
        </div>
      </div>

      {/* CTA area */}
      <div className={styles.ctaArea}>
        <button
          type="button"
          className={styles.ctaPrimary}
          onClick={handleShare}
          data-testid="mp-reveal-share-btn"
        >
          {t('moonPhase.reveal.shareCta')}
        </button>
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={handleCatalog}
          data-testid="mp-reveal-catalog-btn"
        >
          {t('moonPhase.reveal.catalogCta')}
        </button>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------
// Refetch fallback (deep-link or refresh — no router state available)
// --------------------------------------------------------------------------

function RevealFetched(): JSX.Element {
  const { t } = useTranslation('flows');
  const { mutate, data, isPending, isError, error } = useRunMoonPhase();
  const hasFired = useRef(false);

  // Stable ref so useEffect can call mutate without re-running
  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;
    mutate();
  }, [mutate]);

  if (isPending) {
    return (
      <div className={styles.computingCenter} aria-live="polite">
        <div className={styles.sigilWrap}>
          <LivingSigil size={220} state="pulse" archetype="moon_phase_soulmate" />
        </div>
        <p className={styles.computingCaption}>{t('moonPhase.computing.caption')}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorBlock} role="alert">
        <p className={styles.errorMessage}>
          {error?.message ?? t('moonPhase.reveal.loadErrorFallback')}
        </p>
      </div>
    );
  }

  if (data) {
    return <RevealBody output={data.run.output} />;
  }

  return <></>;
}

// --------------------------------------------------------------------------
// Main screen
// --------------------------------------------------------------------------

export function MoonPhaseReveal(): JSX.Element {
  const { t } = useTranslation('flows');
  const location = useLocation();
  const locationState = location.state as { runId?: string; output?: MoonPhaseOutput } | null;
  const outputFromState = locationState?.output ?? null;

  // Haptic on mount
  useEffect(() => {
    try {
      WebApp.HapticFeedback.notificationOccurred('success');
    } catch {
      // not in Telegram context — ignore
    }
  }, []);

  return (
    <div className={styles.root}>
      <CosmosBackdrop />

      <main
        role="main"
        aria-label={t('moonPhase.reveal.mainAria')}
        className={styles.content}
      >
        {outputFromState ? (
          <RevealBody output={outputFromState} />
        ) : (
          <RevealFetched />
        )}
      </main>
    </div>
  );
}
